import { useQuery } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { guestMenuUrl, roomBookingUrl } from '@kortex/shared';
import { api } from '../lib/api';
import { Empty, Loading, Modal } from './ui';
import type { RestaurantTable } from './TableSetup';

interface PropertyProfile {
  name: string;
  city: string;
  menuToken: string;
}

/** The guest-menu address for this browser's origin. */
const menuUrl = (token: string, tableCode?: string): string =>
  guestMenuUrl(window.location.origin, token, tableCode);

/** The public booking address, off the same token. */
const bookingUrl = (token: string): string => roomBookingUrl(window.location.origin, token);

/**
 * Renders the QR itself.
 *
 * The encoder is the `qrcode` library rather than something hand-rolled: a
 * square that does not scan is worse than no square at all, and correctness
 * here cannot be checked by reading the code - only by pointing a phone at it.
 * Error correction is set to M so a smudged or slightly torn card still reads.
 */
function Qr({ url, size = 148 }: { url: string; size?: number }) {
  const svg = useQuery({
    queryKey: ['qr', url, size],
    queryFn: () =>
      QRCode.toString(url, {
        type: 'svg',
        errorCorrectionLevel: 'M',
        margin: 1,
        width: size,
        color: { dark: '#0d2b2a', light: '#ffffff' },
      }),
    staleTime: Infinity,
  });

  if (!svg.data) return <div className="qr-placeholder" style={{ width: size, height: size }} />;
  return <div className="qr" style={{ width: size }} dangerouslySetInnerHTML={{ __html: svg.data }} />;
}

/**
 * A printable sheet of table QR codes.
 *
 * One card per table, cut out and stood on the table. Printing is the browser's
 * job - the print stylesheet drops everything but the cards.
 */
export function TableQrCodes({ onClose }: { onClose: () => void }) {
  const tables = useQuery({
    queryKey: ['tables'],
    queryFn: () => api.get<RestaurantTable[]>('/api/tables'),
  });
  const property = useQuery({
    queryKey: ['property'],
    queryFn: () => api.get<PropertyProfile>('/api/property'),
  });

  const token = property.data?.menuToken;
  const list = tables.data ?? [];

  return (
    <Modal title="Table QR codes" onClose={onClose} size="wide">
      <div className="stack qr-sheet-wrap">
        <section className="card no-print">
          <div className="card-body row between wrap gap-6">
            <div className="small muted" style={{ maxWidth: '52ch' }}>
              Print these, cut them out and stand one on each table. A guest scans it and gets your
              menu — the dishes that are on sale and priced, nothing else.
            </div>
            <button className="btn primary" disabled={!token || list.length === 0} onClick={() => window.print()}>
              Print
            </button>
          </div>
        </section>

        {tables.isLoading || property.isLoading ? (
          <Loading />
        ) : !token ? (
          <Empty message="Could not read this property's menu link." />
        ) : list.length === 0 ? (
          <Empty message="Add some tables first — then every one of them gets a code." />
        ) : (
          <>
            <div className="qr-sheet">
              {list.map((table) => (
                <article key={table.id} className="qr-card">
                  <div className="qr-card-head">
                    <b>{property.data!.name}</b>
                    <span>Table {table.code}</span>
                  </div>
                  <Qr url={menuUrl(token, table.code)} />
                  <div className="qr-card-foot">
                    <b>Scan for the menu</b>
                    <span>{table.section}</span>
                  </div>
                </article>
              ))}
            </div>

            <section className="card no-print">
              <div className="card-head">
                <h3>Menu link</h3>
                <span className="small muted">Same menu, without a table</span>
              </div>
              <div className="card-body col gap-6">
                <code className="mono small">{menuUrl(token)}</code>
                <a
                  className="btn sm"
                  href={menuUrl(token)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ alignSelf: 'flex-start' }}
                >
                  Open what a guest sees
                </a>
                <p className="small muted" style={{ marginTop: 4 }}>
                  This address is whatever you reach SARTHI on. A code printed from a laptop running
                  the app locally will only open on that laptop; print them from the address your
                  staff and guests actually use.
                </p>
              </div>
            </section>

            <section className="card no-print">
              <div className="card-head">
                <h3>Room booking link</h3>
                <span className="small muted">Put this on your website or WhatsApp</span>
              </div>
              <div className="card-body col gap-6">
                <code className="mono small">{bookingUrl(token)}</code>
                <div className="row gap-6 wrap">
                  <a
                    className="btn sm"
                    href={bookingUrl(token)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open the booking page
                  </a>
                  <button
                    className="btn sm"
                    onClick={() => void navigator.clipboard.writeText(bookingUrl(token))}
                  >
                    Copy link
                  </button>
                </div>
                <p className="small muted" style={{ marginTop: 4 }}>
                  Guests pick their dates, see what is free and book without an account. Whether
                  they pay now or at the desk depends on your payment settings.
                </p>
              </div>
            </section>
          </>
        )}
      </div>
    </Modal>
  );
}
